/**
 * Two-pedal input. Keyboard and touch pedals feed the same throttle intent in [-1, 1]:
 * gas = +1, brake = -1, both = 0. In the air, the same intent tilts the buggy.
 */
export class PedalInput {
  private readonly keys = new Set<string>();
  private readonly pointers = new Map<number, 'gas' | 'brake'>();
  private readonly cleanups: Array<() => void> = [];
  onPause: () => void = () => {};
  onRestart: () => void = () => {};

  constructor(
    private readonly gasPedal: HTMLElement,
    private readonly brakePedal: HTMLElement,
  ) {
    this.listen(window, 'keydown', (e) => this.onKey(e as KeyboardEvent, true));
    this.listen(window, 'keyup', (e) => this.onKey(e as KeyboardEvent, false));
    this.listen(window, 'blur', () => this.releaseAll());
    this.listen(document, 'visibilitychange', () => {
      if (document.hidden) this.releaseAll();
    });
    for (const [el, pedal] of [
      [gasPedal, 'gas'],
      [brakePedal, 'brake'],
    ] as const) {
      this.listen(el, 'pointerdown', (e) => this.press(e as PointerEvent, el, pedal));
      for (const type of ['pointerup', 'pointercancel', 'lostpointercapture']) {
        this.listen(el, type, (e) => this.release((e as PointerEvent).pointerId));
      }
      this.listen(el, 'contextmenu', (e) => e.preventDefault());
    }
  }

  get gas(): boolean {
    return this.keys.has('gas') || [...this.pointers.values()].includes('gas');
  }

  get brake(): boolean {
    return this.keys.has('brake') || [...this.pointers.values()].includes('brake');
  }

  get throttle(): number {
    return (this.gas ? 1 : 0) - (this.brake ? 1 : 0);
  }

  dispose(): void {
    for (const off of this.cleanups) off();
    this.cleanups.length = 0;
  }

  private press(e: PointerEvent, el: HTMLElement, pedal: 'gas' | 'brake'): void {
    e.preventDefault();
    try {
      el.setPointerCapture(e.pointerId);
    } catch {
      // Capture is best-effort; release still arrives via pointerup / pointercancel.
    }
    this.pointers.set(e.pointerId, pedal);
    this.syncPedalStates();
  }

  private release(pointerId: number): void {
    this.pointers.delete(pointerId);
    this.syncPedalStates();
  }

  private releaseAll(): void {
    this.keys.clear();
    this.pointers.clear();
    this.syncPedalStates();
  }

  private onKey(e: KeyboardEvent, down: boolean): void {
    if (isTyping(e)) return; // A/D, R, P and Esc belong to the text field while typing a name
    const pedal =
      e.code === 'ArrowRight' || e.code === 'KeyD' ? 'gas' : e.code === 'ArrowLeft' || e.code === 'KeyA' ? 'brake' : null;
    if (pedal) {
      e.preventDefault();
      if (down) this.keys.add(pedal);
      else this.keys.delete(pedal);
      this.syncPedalStates();
      return;
    }
    if (!down || e.repeat) return;
    if (e.code === 'Escape' || e.code === 'KeyP') this.onPause();
    if (e.code === 'KeyR') this.onRestart();
  }

  private syncPedalStates(): void {
    this.gasPedal.classList.toggle('is-pressed', this.gas);
    this.brakePedal.classList.toggle('is-pressed', this.brake);
  }

  private listen(target: EventTarget, type: string, handler: (e: Event) => void): void {
    target.addEventListener(type, handler, { passive: false });
    this.cleanups.push(() => target.removeEventListener(type, handler));
  }
}

/** True when a key goes to a text field (game shortcuts must leave it alone). */
export function isTyping(e: KeyboardEvent): boolean {
  const t = e.target as HTMLElement | null;
  return !!t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable);
}
